// #2121 v2 - Reducer del flujo de derivación de estudios remote-device
import CONSTANTS from '../CONSTANTS';

const initialState = {
  // listado y filtros base
  studies:           [],
  page:              0,
  lastPage:          0,
  limit:             15,
  totalEntries:      0,
  view:              'inbox',           // inbox | assigned | all
  search:            '',
  dateFrom:          null,
  dateTo:            null,
  sortDate:          'desc',
  loadingList:       false,
  // v2 — filtros nuevos
  deviceTypes:        [],               // lista de tipos disponibles (poblada desde el backend)
  deviceTypeFilter:   [],               // tipos seleccionados para filtrar
  assignedDoctorFilter: null,
  // v2 — stats agregados (independientes del view filter)
  stats:              { pending: 0, derivedToday: 0, stale: 0 },
  // v2 — selección múltiple para bulk-reassign
  selectedIds:        [],               // array de _id seleccionados
  // modal de asignación (acepta uno o varios reportIds)
  modalOpen:         false,
  selectedReportId:  null,              // string | string[] | null
  assignableDoctors: [],
  loadingAssignable: false,
  reassigning:       false,
  reassignError:     null,
  // v2 — estado para live updates
  pendingNewCount:    0,                // cuenta de estudios nuevos que no se pudieron prepend (page > 0 o filtro no coincidente)
  // v2 — modal de historial de derivaciones
  historyOpen:       false,
  historyStudyId:    null,
  // misc
  scroll:            0,
};

const upsertStudy = (studies, incoming) => {
  const idx = studies.findIndex((s) => String(s._id) === String(incoming._id));
  if (idx === -1) return [incoming, ...studies];
  const next = [...studies];
  next[idx] = { ...next[idx], ...incoming };
  return next;
};

const removeStudy = (studies, studyId) =>
  studies.filter((s) => String(s._id) !== String(studyId));

const updateStudyFields = (studies, studyId, patch) => {
  const idx = studies.findIndex((s) => String(s._id) === String(studyId));
  if (idx === -1) return studies;
  const next = [...studies];
  next[idx] = { ...next[idx], ...patch };
  return next;
};

const deviceStudiesReducer = (state = initialState, action) => {
  switch (action.type) {

    case CONSTANTS.DEVICE_SET_LOADING_LIST:
      return { ...state, loadingList: action.value };

    case CONSTANTS.DEVICE_SET_STUDIES:
      return {
        ...state,
        studies:         action.payload.data,
        page:            action.payload.page,
        lastPage:        action.payload.lastPage,
        limit:           action.payload.limit,
        totalEntries:    action.payload.total,
        loadingList:     false,
        pendingNewCount: 0,                  // al refrescar, limpiar contador de pendientes
      };

    case CONSTANTS.DEVICE_SET_VIEW:
      return { ...state, view: action.view, page: 0, selectedIds: [], pendingNewCount: 0 };

    case CONSTANTS.DEVICE_SET_SEARCH:
      return { ...state, search: action.search, page: 0, selectedIds: [], pendingNewCount: 0 };

    case CONSTANTS.DEVICE_SET_DATE_RANGE:
      return { ...state, dateFrom: action.from, dateTo: action.to, page: 0, selectedIds: [], pendingNewCount: 0 };

    case CONSTANTS.DEVICE_SET_TYPE_FILTER:
      return { ...state, deviceTypeFilter: action.deviceTypes || [], page: 0, selectedIds: [], pendingNewCount: 0 };

    case CONSTANTS.DEVICE_SET_ASSIGNED_FILTER:
      return { ...state, assignedDoctorFilter: action.assignedDoctor || null, page: 0, selectedIds: [], pendingNewCount: 0 };

    case CONSTANTS.DEVICE_SET_TYPES:
      return { ...state, deviceTypes: action.types || [] };

    case CONSTANTS.DEVICE_SET_STATS:
      return { ...state, stats: action.stats || state.stats };

    case CONSTANTS.DEVICE_SET_PAGE_INDEX:
      return { ...state, page: action.val, selectedIds: [], pendingNewCount: 0 };

    // ---- selección múltiple ----
    case CONSTANTS.DEVICE_TOGGLE_SELECT: {
      const id = String(action.studyId);
      const has = state.selectedIds.includes(id);
      return { ...state, selectedIds: has ? state.selectedIds.filter((x) => x !== id) : [...state.selectedIds, id] };
    }
    case CONSTANTS.DEVICE_CLEAR_SELECTION:
      return { ...state, selectedIds: [] };
    case CONSTANTS.DEVICE_SELECT_ALL_VISIBLE:
      return { ...state, selectedIds: (action.studyIds || []).map(String) };

    // ---- modal ----
    case CONSTANTS.DEVICE_OPEN_ASSIGN_MODAL:
      return {
        ...state,
        modalOpen:         true,
        selectedReportId:  action.reportId,
        reassignError:     null,
        assignableDoctors: [],
      };

    case CONSTANTS.DEVICE_CLOSE_ASSIGN_MODAL:
      return {
        ...state,
        modalOpen:        false,
        selectedReportId: null,
        reassignError:    null,
        reassigning:      false,
      };

    case CONSTANTS.DEVICE_SET_ASSIGNABLE:
      return { ...state, assignableDoctors: action.list, loadingAssignable: false };

    case CONSTANTS.DEVICE_SET_LOADING_ASSIGN:
      return { ...state, loadingAssignable: action.value };

    // ---- single reassign ----
    case CONSTANTS.DEVICE_REASSIGN:
      return { ...state, reassigning: true, reassignError: null };
    case CONSTANTS.DEVICE_REASSIGN_SUCCESS:
      return { ...state, reassigning: false, modalOpen: false, reassignError: null };
    case CONSTANTS.DEVICE_REASSIGN_ERROR:
      return { ...state, reassigning: false, reassignError: action.code };

    // ---- bulk ----
    case CONSTANTS.DEVICE_BULK_REASSIGN:
      return { ...state, reassigning: true, reassignError: null };
    case CONSTANTS.DEVICE_BULK_REASSIGN_OK:
      return { ...state, reassigning: false, modalOpen: false, reassignError: null, selectedIds: [] };
    case CONSTANTS.DEVICE_BULK_REASSIGN_ERR:
      return { ...state, reassigning: false, reassignError: action.code };

    // ---- WS live updates ----
    case CONSTANTS.DEVICE_WS_INCOMING: {
      const incoming = action.payload;
      // Si la vista actual no es inbox/all, no aplicar (no tiene por qué aparecer)
      if (state.view === 'assigned') return state;
      // Si está en página > 0, no prepend; incrementar contador
      if (state.page > 0) return { ...state, pendingNewCount: state.pendingNewCount + 1 };
      // Si hay filtros activos que probablemente excluyen, también incrementar contador
      const passesTypeFilter = state.deviceTypeFilter.length === 0 ||
        state.deviceTypeFilter.includes(incoming.device_type || incoming.deviceType);
      if (!passesTypeFilter) return { ...state, pendingNewCount: state.pendingNewCount + 1 };
      return {
        ...state,
        studies:      upsertStudy(state.studies, incoming).slice(0, state.limit),
        totalEntries: state.totalEntries + 1,
      };
    }

    case CONSTANTS.DEVICE_WS_REASSIGNED: {
      const { studyId, derivations, assigned_doctor_name, assigned_doctor_id } = action.payload;
      // En vista inbox: si tiene derivaciones, lo sacamos
      if (state.view === 'inbox' && derivations && derivations.length > 0) {
        return {
          ...state,
          studies: removeStudy(state.studies, studyId),
          totalEntries: Math.max(0, state.totalEntries - 1),
          selectedIds: state.selectedIds.filter((id) => id !== String(studyId)),
        };
      }
      // En vista assigned/all: actualizamos el row
      return {
        ...state,
        studies: updateStudyFields(state.studies, studyId, {
          derivations,
          assigned_doctor_id,
          assigned_doctor_name,
        }),
      };
    }

    case CONSTANTS.DEVICE_WS_REASSIGNED_BULK: {
      const { studyIds = [] } = action.payload || {};
      if (state.view === 'inbox') {
        const idsSet = new Set(studyIds.map(String));
        const filtered = state.studies.filter((s) => !idsSet.has(String(s._id)));
        return {
          ...state,
          studies: filtered,
          totalEntries: Math.max(0, state.totalEntries - studyIds.length),
          selectedIds: state.selectedIds.filter((id) => !idsSet.has(id)),
        };
      }
      return state;
    }

    case CONSTANTS.DEVICE_APPLY_PENDING_NEW:
      // Saga se encarga de re-fetch; reducer solo limpia contador
      return { ...state, pendingNewCount: 0 };

    case CONSTANTS.DEVICE_OPEN_HISTORY:
      return { ...state, historyOpen: true, historyStudyId: action.studyId };

    case CONSTANTS.DEVICE_CLOSE_HISTORY:
      return { ...state, historyOpen: false, historyStudyId: null };

    case CONSTANTS.DEVICE_SAVE_SCROLL:
      return { ...state, scroll: action.scroll };

    default:
      return state;
  }
};

export default deviceStudiesReducer;
// #2121 - End
